import { useEffect, useMemo, useRef, useState } from 'react'
import { isAxiosError } from 'axios'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { BomImportDropZone } from '@/shared/components/bom/BomImportDropZone'
import {
  useParsePackagingShipmentSkuFile,
  useReviewPackagingShipmentSkus,
  useUpdatePackagingShipment,
  type ShipmentSkuFileNeedsColumn,
  type ShipmentSkuFilePhase,
} from '@/shared/hooks/api/packagingShipments'
import { MAX_UPLOAD_FILE_SIZE_BYTES } from '@/shared/lib/uploadGuardrails'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import { ConfirmDialog } from '@/components/confirm-dialog'
import { ShipmentSkuColumnMappingDialog } from './ShipmentSkuColumnMappingDialog'
import { ShipmentSkuMatchReview } from './ShipmentSkuMatchReview'
import {
  pastedShipmentSkus,
  SHIPMENT_PASTE_MAX_UNIQUE_SKUS,
  uniqueShipmentSkus,
  type ShipmentSkuReviewItem,
} from './ShipmentSkuReviewList'
import { skuFileErrorMessage } from './shipmentSkuFileError'
import {
  isAllowedShipmentSkuFile,
  SHIPMENT_SKU_FILE_ACCEPT,
} from './shipmentSkuFileTypes'

type EditShipmentDialogMode = 'name' | 'note' | 'skus'

type EditShipmentDialogProps = {
  shipmentId: string
  open: boolean
  mode: EditShipmentDialogMode
  name: string
  note: string
  onOpenChange: (open: boolean) => void
}

export function EditShipmentDialog({
  shipmentId,
  open,
  mode,
  name: initialName,
  note: initialNote,
  onOpenChange,
}: EditShipmentDialogProps) {
  const { t } = useTranslation(['packagingSuppliers', 'common'])
  const updateShipment = useUpdatePackagingShipment(shipmentId)
  const parseSkuFile = useParsePackagingShipmentSkuFile()
  const reviewSkus = useReviewPackagingShipmentSkus()
  const [name, setName] = useState(initialName)
  const [note, setNote] = useState(initialNote)
  const [formError, setFormError] = useState<string | null>(null)
  const [step, setStep] = useState<'details' | 'review'>('details')
  const [reviewItems, setReviewItems] = useState<
    ShipmentSkuReviewItem[] | null
  >(null)
  const [extractedSkus, setExtractedSkus] = useState<string[] | null>(null)
  const [replaceConfirmOpen, setReplaceConfirmOpen] = useState(false)
  const [pasteText, setPasteText] = useState('')
  const [skuEntry, setSkuEntry] = useState<'upload' | 'paste'>('upload')
  const [showDropZone, setShowDropZone] = useState(true)
  const [selectedFileName, setSelectedFileName] = useState<string | null>(null)
  const [columnPrompt, setColumnPrompt] =
    useState<ShipmentSkuFileNeedsColumn | null>(null)
  const [pendingFileKey, setPendingFileKey] = useState<string | null>(null)
  const [uploadFileError, setUploadFileError] = useState<string | null>(null)
  const [skuFilePhase, setSkuFilePhase] = useState<ShipmentSkuFilePhase | null>(
    null
  )
  const uploadGenerationRef = useRef(0)

  useEffect(() => {
    uploadGenerationRef.current += 1
    if (!open) {
      return
    }
    setName(initialName)
    setNote(initialNote)
    setFormError(null)
    setStep('details')
    setReviewItems(null)
    setExtractedSkus(null)
    setReplaceConfirmOpen(false)
    setPasteText('')
    setSkuEntry('upload')
    setShowDropZone(true)
    setSelectedFileName(null)
    setColumnPrompt(null)
    setPendingFileKey(null)
    setUploadFileError(null)
    setSkuFilePhase(null)
  }, [initialName, initialNote, mode, open])

  const editErrorMessage = (error: unknown): string => {
    const serverMessage =
      isAxiosError(error) && typeof error.response?.data?.message === 'string'
        ? error.response.data.message
        : null
    if (serverMessage === 'Enter a name.') {
      return t('shipmentsPage.skuReview.enterName')
    }
    if (serverMessage === 'Name must be 200 characters or fewer.') {
      return t('shipmentsPage.skuReview.nameTooLong')
    }
    if (serverMessage === 'Note must be 2,000 characters or fewer.') {
      return t('shipmentsPage.skuReview.noteTooLong')
    }
    if (serverMessage === 'At least one matched SKU is required.') {
      return t('shipmentsPage.skuReview.matchedRequired')
    }
    if (serverMessage === 'A check is already running.') {
      return t('shipmentsPage.detail.checkAlreadyRunning')
    }
    if (serverMessage === 'Those SKUs are already on this shipment.') {
      return t('shipmentsPage.detail.skusAlreadyOnShipment')
    }
    return t('shipmentsPage.detail.editFailed')
  }

  const replaceSkus = () => {
    if (reviewItems == null) {
      return
    }
    setFormError(null)
    updateShipment.mutate(
      {
        name: initialName,
        note: initialNote,
        skus: reviewItems.map((item) => item.sku),
      },
      {
        onSuccess: () => {
          setReplaceConfirmOpen(false)
          onOpenChange(false)
        },
        onError: (error) => {
          const message = editErrorMessage(error)
          setFormError(message)
          setReplaceConfirmOpen(false)
          toast.error(message)
        },
      }
    )
  }

  const appendSkus = () => {
    if (reviewItems == null) {
      return
    }
    setFormError(null)
    updateShipment.mutate(
      {
        name: initialName,
        note: initialNote,
        appendSkus: reviewItems.map((item) => item.sku),
      },
      {
        onSuccess: () => {
          onOpenChange(false)
        },
        onError: (error) => {
          const message = editErrorMessage(error)
          setFormError(message)
          toast.error(message)
        },
      }
    )
  }

  const saveField = () => {
    let validationError: string | null = null
    if (mode === 'name') {
      const trimmedName = name.trim()
      if (trimmedName.length === 0) {
        validationError = t('shipmentsPage.skuReview.enterName')
      } else if (trimmedName.length > 200) {
        validationError = t('shipmentsPage.skuReview.nameTooLong')
      }
    } else if (note.length > 2000) {
      validationError = t('shipmentsPage.skuReview.noteTooLong')
    }
    if (validationError != null) {
      setFormError(validationError)
      return
    }
    setFormError(null)
    let payload = { name: initialName, note }
    if (mode === 'name') {
      payload = { name: name.trim(), note: initialNote }
    }
    updateShipment.mutate(payload, {
      onSuccess: () => onOpenChange(false),
      onError: (error) => {
        const message = editErrorMessage(error)
        setFormError(message)
        toast.error(message)
      },
    })
  }

  const applyParsedFile = (
    result: Awaited<ReturnType<typeof parseSkuFile.mutateAsync>>
  ) => {
    if (result.outcome === 'needsColumn') {
      setPendingFileKey(result.fileKey)
      setColumnPrompt(result)
      setExtractedSkus(null)
      setReviewItems(null)
      setShowDropZone(false)
      return
    }
    setColumnPrompt(null)
    setPendingFileKey(null)
    setExtractedSkus(uniqueShipmentSkus(result.skus))
    setReviewItems(result.items)
    setShowDropZone(false)
  }

  const clearParsedFile = () => {
    setExtractedSkus(null)
    setReviewItems(null)
    setColumnPrompt(null)
    setPendingFileKey(null)
    setSkuFilePhase(null)
    setShowDropZone(true)
  }

  const parseSelectedFile = async (file: File) => {
    const generation = uploadGenerationRef.current + 1
    uploadGenerationRef.current = generation
    setUploadFileError(null)
    if (file.size > MAX_UPLOAD_FILE_SIZE_BYTES) {
      setUploadFileError(t('shipmentsPage.skuFile.tooLarge'))
      clearParsedFile()
      return
    }
    try {
      const result = await parseSkuFile.mutateAsync({
        file,
        onPhaseChange: (phase) => {
          if (uploadGenerationRef.current === generation) {
            setSkuFilePhase(phase)
          }
        },
      })
      if (uploadGenerationRef.current !== generation) {
        return
      }
      applyParsedFile(result)
    } catch (error) {
      if (uploadGenerationRef.current !== generation) {
        return
      }
      clearParsedFile()
      setUploadFileError(skuFileErrorMessage(error, t))
    } finally {
      if (uploadGenerationRef.current === generation) {
        setSkuFilePhase(null)
      }
    }
  }

  const parseChosenColumn = (skuColumn: number) => {
    if (pendingFileKey == null) {
      return
    }
    const generation = uploadGenerationRef.current + 1
    uploadGenerationRef.current = generation
    setUploadFileError(null)
    setSkuFilePhase('reading')
    void parseSkuFile
      .mutateAsync({ fileKey: pendingFileKey, skuColumn })
      .then((result) => {
        if (uploadGenerationRef.current !== generation) {
          return
        }
        applyParsedFile(result)
      })
      .catch((error: unknown) => {
        if (uploadGenerationRef.current !== generation) {
          return
        }
        clearParsedFile()
        setUploadFileError(skuFileErrorMessage(error, t))
      })
      .finally(() => {
        if (uploadGenerationRef.current === generation) {
          setSkuFilePhase(null)
        }
      })
  }

  const pastedSkus = useMemo(() => pastedShipmentSkus(pasteText), [pasteText])

  const selectSkuEntry = (value: string) => {
    if (value !== 'upload' && value !== 'paste') {
      return
    }
    if (value === skuEntry) {
      return
    }
    uploadGenerationRef.current += 1
    setReviewItems(null)
    setStep('details')
    setReplaceConfirmOpen(false)
    setColumnPrompt(null)
    setPendingFileKey(null)
    setSkuFilePhase(null)
    setUploadFileError(null)
    setFormError(null)
    if (value === 'upload') {
      setPasteText('')
    }
    setSkuEntry(value)
  }

  const skusForReview = skuEntry === 'paste' ? pastedSkus : (extractedSkus ?? [])
  const pasteOverLimit =
    skuEntry === 'paste' && pastedSkus.length > SHIPMENT_PASTE_MAX_UNIQUE_SKUS
  const canReviewSkus =
    skusForReview.length > 0 &&
    !pasteOverLimit &&
    !parseSkuFile.isPending &&
    !reviewSkus.isPending &&
    !updateShipment.isPending
  const noneMatched =
    reviewItems != null && reviewItems.every((item) => !item.matched)
  const showingReview = step === 'review' && reviewItems != null && mode === 'skus'
  const showingName = mode === 'name'
  const showingNote = mode === 'note'
  const showingSkus = mode === 'skus' && !showingReview
  let dialogTitle = t('shipmentsPage.detail.updateSkus')
  if (showingReview) {
    dialogTitle = t('shipmentsPage.skuFile.reviewTitle')
  } else if (showingName) {
    dialogTitle = t('shipmentsPage.detail.editNameTitle')
  } else if (showingNote) {
    dialogTitle = t('shipmentsPage.detail.editNoteTitle')
  }
  const nameUnchanged = name.trim() === initialName.trim()
  const noteUnchanged = note.trim() === initialNote.trim()
  const uploadBusyLabel = parseSkuFile.isPending
    ? skuFilePhase === 'uploading'
      ? t('shipmentsPage.skuFile.uploading')
      : t('shipmentsPage.skuFile.reading')
    : undefined

  const reviewSkusStep = async () => {
    if (skusForReview.length === 0 || pasteOverLimit) {
      return
    }
    setFormError(null)
    if (reviewItems != null) {
      setStep('review')
      return
    }
    try {
      const review = await reviewSkus.mutateAsync({ skus: skusForReview })
      setReviewItems(review.items)
      setStep('review')
    } catch (error) {
      const serverMessage =
        isAxiosError(error) && typeof error.response?.data?.message === 'string'
          ? error.response.data.message
          : null
      setFormError(
        serverMessage === 'Workbook has more than 10000 unique SKUs'
          ? t('shipmentsPage.skuFile.pasteTooManyUniqueSkus')
          : t('shipmentsPage.skuReview.reviewFailed')
      )
    }
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className={
            showingReview
              ? 'flex max-h-[calc(100vh-2rem)] flex-col overflow-hidden sm:max-w-2xl'
              : 'max-h-[calc(100vh-2rem)] overflow-y-auto'
          }
        >
          <DialogHeader>
            <DialogTitle>{dialogTitle}</DialogTitle>
          </DialogHeader>
          {showingReview && reviewItems != null ? (
            <ShipmentSkuMatchReview items={reviewItems} />
          ) : (
          <div className='space-y-4'>
            {showingName && (
              <div className='space-y-1'>
                <Label htmlFor='edit-shipment-name'>
                  {t('shipmentsPage.form.name')}
                </Label>
                <Input
                  id='edit-shipment-name'
                  value={name}
                  autoFocus
                  onChange={(event) => setName(event.target.value)}
                />
              </div>
            )}
            {showingNote && (
              <div className='space-y-1'>
                <Label htmlFor='edit-shipment-note'>
                  {t('shipmentsPage.form.note')}
                </Label>
                <Textarea
                  id='edit-shipment-note'
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                  rows={3}
                  autoFocus
                  className='field-sizing-fixed h-24 resize-none overflow-y-auto'
                />
                <p className='text-sm text-muted-foreground'>
                  {t('shipmentsPage.form.noteHint')}
                </p>
              </div>
            )}
            {showingSkus && (
            <Tabs value={skuEntry} onValueChange={selectSkuEntry}>
              <TabsList className='grid w-full grid-cols-2'>
                <TabsTrigger value='upload'>
                  {t('shipmentsPage.skuFile.uploadTab')}
                </TabsTrigger>
                <TabsTrigger value='paste'>
                  {t('shipmentsPage.skuFile.pasteTab')}
                </TabsTrigger>
              </TabsList>
              <TabsContent value='upload' className='space-y-2 pt-2'>
                <p className='text-sm text-muted-foreground'>
                  {t('shipmentsPage.skuFile.uploadReplacesHint')}
                </p>
                {showDropZone ? (
                  <BomImportDropZone
                    accept={SHIPMENT_SKU_FILE_ACCEPT}
                    isBusy={parseSkuFile.isPending}
                    busyLabel={uploadBusyLabel}
                    fileError={uploadFileError}
                    labels={{
                      formatsRequirement: t(
                        'shipmentsPage.skuFile.uploadFormatsRequirement'
                      ),
                      dragDrop: t('shipmentsPage.skuFile.uploadDragDrop'),
                      orClick: t('shipmentsPage.skuFile.uploadOrClick'),
                      selectFile: t('shipmentsPage.skuFile.uploadSelectFile'),
                    }}
                    onFileSelected={(file) => {
                      if (!isAllowedShipmentSkuFile(file)) {
                        setUploadFileError(
                          t('shipmentsPage.skuFile.unreadable')
                        )
                        return
                      }
                      setSelectedFileName(file.name)
                      void parseSelectedFile(file)
                    }}
                  />
                ) : (
                  <div className='flex flex-wrap items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm'>
                    <div className='min-w-0'>
                      <p className='truncate font-medium'>{selectedFileName}</p>
                      {uploadBusyLabel != null && (
                        <p
                          className='flex items-center gap-2 text-muted-foreground'
                          role='status'
                        >
                          <Loader2
                            className='size-4 animate-spin'
                            aria-hidden
                          />
                          {uploadBusyLabel}
                        </p>
                      )}
                      {extractedSkus != null && (
                        <p className='text-muted-foreground'>
                          {t('shipmentsPage.skuFile.uploadedSkuCount', {
                            count: extractedSkus.length,
                          })}
                        </p>
                      )}
                    </div>
                    <Button
                      type='button'
                      variant='outline'
                      size='sm'
                      disabled={parseSkuFile.isPending}
                      onClick={() => {
                        setShowDropZone(true)
                        setExtractedSkus(null)
                        setReviewItems(null)
                        setColumnPrompt(null)
                        setUploadFileError(null)
                      }}
                    >
                      {t('shipmentsPage.skuFile.replaceFile')}
                    </Button>
                  </div>
                )}
              </TabsContent>
              <TabsContent value='paste' className='space-y-2 pt-2'>
                <p className='text-sm text-muted-foreground'>
                  {t('shipmentsPage.skuFile.pasteAppendsHint')}
                </p>
                <Label htmlFor='edit-shipment-paste-skus'>
                  {t('shipmentsPage.skuFile.pasteLabel')}
                </Label>
                <Textarea
                  id='edit-shipment-paste-skus'
                  value={pasteText}
                  rows={6}
                  className='field-sizing-fixed max-h-40 min-h-28 overflow-y-auto'
                  onChange={(event) => {
                    setPasteText(event.target.value)
                    setReviewItems(null)
                  }}
                />
                {pastedSkus.length > 0 && (
                  <p className='text-sm text-muted-foreground'>
                    {t('shipmentsPage.skuFile.pasteSkuCount', {
                      count: pastedSkus.length,
                    })}
                  </p>
                )}
                {pastedSkus.length > SHIPMENT_PASTE_MAX_UNIQUE_SKUS && (
                  <p className='text-sm text-destructive' role='alert'>
                    {t('shipmentsPage.skuFile.pasteTooManyUniqueSkus')}
                  </p>
                )}
              </TabsContent>
            </Tabs>
            )}
            {formError != null && (
              <p className='text-sm text-destructive' role='alert'>
                {formError}
              </p>
            )}
          </div>
          )}
          {showingReview && formError != null && (
            <p className='text-sm text-destructive' role='alert'>
              {formError}
            </p>
          )}
          <DialogFooter>
            {showingReview ? (
              <>
                <Button
                  type='button'
                  variant='outline'
                  onClick={() => setStep('details')}
                >
                  {t('shipmentsPage.skuReview.back')}
                </Button>
                {skuEntry === 'upload' ? (
                  <Button
                    type='button'
                    disabled={noneMatched || updateShipment.isPending}
                    onClick={() => setReplaceConfirmOpen(true)}
                  >
                    {t('shipmentsPage.detail.replaceSkusConfirm')}
                  </Button>
                ) : (
                  <Button
                    type='button'
                    disabled={noneMatched || updateShipment.isPending}
                    onClick={appendSkus}
                  >
                    {t('shipmentsPage.skuReview.addSkus')}
                  </Button>
                )}
              </>
            ) : showingName || showingNote ? (
              <>
                <Button
                  variant='outline'
                  onClick={() => onOpenChange(false)}
                  disabled={updateShipment.isPending}
                >
                  {t('common:actions.cancel')}
                </Button>
                <Button
                  type='button'
                  disabled={
                    updateShipment.isPending ||
                    (showingName &&
                      (name.trim().length === 0 || nameUnchanged)) ||
                    (showingNote && noteUnchanged)
                  }
                  onClick={saveField}
                >
                  {t('shipmentsPage.detail.saveDetails')}
                </Button>
              </>
            ) : (
              <>
                <Button variant='outline' onClick={() => onOpenChange(false)}>
                  {t('common:actions.cancel')}
                </Button>
                <Button
                  type='button'
                  disabled={!canReviewSkus}
                  onClick={() => {
                    void reviewSkusStep()
                  }}
                >
                  {reviewSkus.isPending
                    ? t('shipmentsPage.skuFile.matching')
                    : t('shipmentsPage.skuFile.reviewSkus')}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ConfirmDialog
        open={replaceConfirmOpen}
        onOpenChange={setReplaceConfirmOpen}
        title={t('shipmentsPage.detail.replaceSkusConfirmTitle')}
        desc={t('shipmentsPage.detail.replaceSkusConfirmDesc')}
        cancelBtnText={t('shipmentsPage.detail.cancel')}
        confirmText={t('shipmentsPage.detail.replaceSkusConfirm')}
        isLoading={updateShipment.isPending}
        handleConfirm={replaceSkus}
      />
      {columnPrompt != null && (
        <ShipmentSkuColumnMappingDialog
          open
          columns={columnPrompt.columns}
          sampleRows={columnPrompt.sampleRows}
          isPending={parseSkuFile.isPending}
          onOpenChange={(next) => {
            if (!next) {
              setColumnPrompt(null)
            }
          }}
          onConfirm={parseChosenColumn}
        />
      )}
    </>
  )
}
